import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV = [
  { id: "services", label: "Programs" },
  { id: "coach", label: "Coach" },
  { id: "method", label: "Process" },
  { id: "platform", label: "Platform" },
  { id: "calculator", label: "Calculator" },
  { id: "faq", label: "FAQ" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    if (!onHome) {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goHome = () => {
    setOpen(false);
    if (onHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 h-16 lg:h-20 flex items-center justify-between">
        <button
          onClick={goHome}
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="/brand/logo.png"
            alt="Precision Running logo"
            className="h-9 w-9 lg:h-10 lg:w-10 object-contain"
          />
          <span className="font-display text-2xl lg:text-3xl tracking-wider text-white group-hover:text-brand-red transition-colors">
            PRECISION<span className="text-brand-red">/</span>RUNNING
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) =>
            n.route ? (
              <Link
                key={n.id}
                to={n.route}
                onClick={() => setOpen(false)}
                data-testid={`nav-link-${n.id}`}
                className="text-xs uppercase tracking-[0.22em] font-bold text-white/70 hover:text-white transition-colors"
              >
                {n.label}
              </Link>
            ) : (
              <button
                key={n.id}
                onClick={() => goTo(n.id)}
                data-testid={`nav-link-${n.id}`}
                className="text-xs uppercase tracking-[0.22em] font-bold text-white/70 hover:text-white transition-colors"
              >
                {n.label}
              </button>
            )
          )}
          <button
            onClick={() => goTo("contact")}
            data-testid="nav-cta-apply"
            className="btn-primary text-xs px-6 py-3"
          >
            Apply <span aria-hidden>→</span>
          </button>
        </nav>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden backdrop-blur-xl bg-black/90 border-t border-white/10"
        >
          <div className="px-4 py-6 flex flex-col gap-4">
            {NAV.map((n) =>
              n.route ? (
                <Link
                  key={n.id}
                  to={n.route}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${n.id}`}
                  className="text-left text-sm uppercase tracking-[0.22em] font-bold text-white/80 hover:text-brand-red"
                >
                  {n.label}
                </Link>
              ) : (
                <button
                  key={n.id}
                  onClick={() => goTo(n.id)}
                  data-testid={`nav-mobile-link-${n.id}`}
                  className="text-left text-sm uppercase tracking-[0.22em] font-bold text-white/80 hover:text-brand-red"
                >
                  {n.label}
                </button>
              )
            )}
            <button
              onClick={() => goTo("contact")}
              data-testid="nav-mobile-cta"
              className="btn-primary mt-2"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
